import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { MessageSquare, Send, X, User, Store, ChevronDown } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PublicChatWidget = ({ storeDomain, storeName, primaryColor }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [nameSet, setNameSet] = useState(false);
  const [sessionId] = useState(() => {
    const stored = localStorage.getItem(`chat-session-${storeDomain}`);
    if (stored) return stored;
    const id = `pub_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem(`chat-session-${storeDomain}`, id);
    return id;
  });
  const messagesEndRef = useRef(null);
  const [hasNewMsg, setHasNewMsg] = useState(false);
  const pc = primaryColor || '#16a34a';

  useEffect(() => {
    const savedName = localStorage.getItem(`chat-name-${storeDomain}`);
    if (savedName) { setCustomerName(savedName); setNameSet(true); }
  }, [storeDomain]);

  useEffect(() => {
    if (isOpen && nameSet) {
      loadMessages();
      const interval = setInterval(loadMessages, 4000);
      return () => clearInterval(interval);
    }
  }, [isOpen, nameSet]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadMessages = async () => {
    try {
      const res = await axios.get(`${API}/public/chat/${storeDomain}/${sessionId}`);
      const prev = messages.length;
      setMessages(res.data);
      if (res.data.length > prev && res.data[res.data.length - 1]?.sender === 'admin') {
        setHasNewMsg(true);
      }
    } catch (e) { /* ignore */ }
  };

  const handleSetName = (e) => {
    e.preventDefault();
    if (!customerName.trim()) return;
    localStorage.setItem(`chat-name-${storeDomain}`, customerName);
    setNameSet(true);
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    try {
      await axios.post(`${API}/public/chat/${storeDomain}`, {
        session_id: sessionId,
        sender: 'customer',
        message: input,
        customer_name: customerName
      });
      setInput('');
      loadMessages();
    } catch (err) { /* ignore */ }
  };

  return (
    <>
      {/* Chat Button */}
      {!isOpen && (
        <button onClick={() => { setIsOpen(true); setHasNewMsg(false); }}
          style={{
            position: 'fixed', bottom: '24px', left: '24px', width: '60px', height: '60px',
            borderRadius: '50%', background: pc, color: 'white', border: 'none', cursor: 'pointer',
            boxShadow: '0 4px 20px rgba(0,0,0,0.2)', zIndex: 40, display: 'flex',
            alignItems: 'center', justifyContent: 'center', transition: 'transform 0.2s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
          onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
          data-testid="chat-widget-btn">
          <MessageSquare size={28} />
          {hasNewMsg && (
            <span style={{ position: 'absolute', top: '-2px', right: '-2px', width: '16px', height: '16px', borderRadius: '50%', background: '#ef4444', border: '2px solid white' }} />
          )}
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div style={{
          position: 'fixed', bottom: '24px', left: '24px', width: '360px', maxWidth: 'calc(100vw - 32px)',
          height: '500px', maxHeight: 'calc(100vh - 100px)', borderRadius: '16px', overflow: 'hidden',
          boxShadow: '0 12px 40px rgba(0,0,0,0.15)', zIndex: 50, display: 'flex', flexDirection: 'column',
          background: 'white', border: '1px solid #e5e7eb'
        }} data-testid="chat-window">
          {/* Header */}
          <div style={{ padding: '16px', background: pc, color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Store size={22} />
              <div>
                <p style={{ fontWeight: 700, fontSize: '0.95rem' }}>{storeName}</p>
                <p style={{ fontSize: '0.75rem', opacity: 0.85 }}>Chat en línea</p>
              </div>
            </div>
            <button onClick={() => setIsOpen(false)} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', padding: '6px', borderRadius: '8px', cursor: 'pointer' }}
              data-testid="chat-close-btn">
              <ChevronDown size={20} />
            </button>
          </div>

          {!nameSet ? (
            /* Name Form */
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
              <form onSubmit={handleSetName} style={{ width: '100%', textAlign: 'center' }}>
                <User size={48} style={{ color: '#ddd', margin: '0 auto 16px' }} />
                <p style={{ fontWeight: 600, marginBottom: '4px', color: '#333' }}>Tu nombre</p>
                <p style={{ fontSize: '0.85rem', color: '#888', marginBottom: '16px' }}>Para que la tienda sepa quién escribe</p>
                <input type="text" value={customerName} onChange={(e) => setCustomerName(e.target.value)} required
                  placeholder="Ej: Juan"
                  style={{ width: '100%', padding: '12px 16px', borderRadius: '10px', border: '1px solid #e5e7eb', fontSize: '0.95rem', outline: 'none', marginBottom: '12px' }}
                  data-testid="chat-name-input" />
                <button type="submit" style={{ width: '100%', padding: '12px', borderRadius: '10px', background: pc, color: 'white', border: 'none', cursor: 'pointer', fontWeight: 600 }}
                  data-testid="chat-name-submit">
                  Empezar Chat
                </button>
              </form>
            </div>
          ) : (
            <>
              {/* Messages */}
              <div style={{ flex: 1, overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {messages.length === 0 && (
                  <div style={{ textAlign: 'center', marginTop: '40px', color: '#999' }}>
                    <MessageSquare size={40} style={{ margin: '0 auto 8px', opacity: 0.4 }} />
                    <p style={{ fontSize: '0.9rem' }}>Escribe tu mensaje</p>
                    <p style={{ fontSize: '0.8rem' }}>Te responderemos lo antes posible</p>
                  </div>
                )}
                {messages.map((msg) => (
                  <div key={msg.id} style={{ display: 'flex', justifyContent: msg.sender === 'customer' ? 'flex-end' : 'flex-start' }}>
                    <div style={{
                      maxWidth: '80%', padding: '10px 14px', borderRadius: '12px',
                      background: msg.sender === 'customer' ? pc : '#f3f4f6',
                      color: msg.sender === 'customer' ? 'white' : '#333'
                    }}>
                      <p style={{ fontSize: '0.9rem', wordBreak: 'break-word' }}>{msg.message}</p>
                      <p style={{ fontSize: '0.65rem', opacity: 0.7, textAlign: 'right', marginTop: '4px' }}>
                        {new Date(msg.timestamp).toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <form onSubmit={handleSend} style={{ padding: '12px', borderTop: '1px solid #e5e7eb', display: 'flex', gap: '8px' }}>
                <input type="text" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Escribe un mensaje..."
                  style={{ flex: 1, padding: '10px 14px', borderRadius: '10px', border: '1px solid #e5e7eb', fontSize: '0.9rem', outline: 'none' }}
                  data-testid="chat-message-input" />
                <button type="submit" style={{ padding: '10px 16px', borderRadius: '10px', background: pc, color: 'white', border: 'none', cursor: 'pointer' }}
                  data-testid="chat-send-btn">
                  <Send size={18} />
                </button>
              </form>
            </>
          )}
        </div>
      )}
    </>
  );
};

export default PublicChatWidget;
